// Client loader: turns the Notion availability rows (kept live by the
// fft-scheduling Worker: Peek -> D1 -> Notion) into PlanSlot view models the
// Plan views render. The Worker means we DON'T need the (unbuilt) Peek
// timeslots fetch for a working schedule board — slot inventory + live guest
// counts already flow through the same Bookable Tours 2026 DB.
//
// See REBUILD-SPEC.md §4/§5; the ✨/❌ availability columns are parsed here.

import type { Market, Region } from "@/types";
import { canonMarket } from "@/lib/markets";
import { slotStartMin } from "@/lib/dates";
import { AVAIL_FIRST_TO_NAME, guidePageIdByPeekName } from "@/config/notion";
import { lookupGuideByAnyName, guideByPeekId, GUIDES } from "@/config/guides";
import { notionExcluded } from "@/config/exclusions";

/** The subset of a Guide the views + publish path need. */
export interface GuideRef {
  id: string; // Peek accountUserId
  peekName: string;
  availName: string;
  region: Region;
  /** Notion guide page id for the Assigned Guide relation; null if unmapped. */
  pageId: string | null;
}

/** A schedulable slot, projected from one Bookable Tours row. */
export interface PlanSlot {
  pageId: string;
  peekTimeslotId: string;
  slotKey: string; // Worker's market|date|HH:MM|CODE — resolves the Peek timeslot for assign
  title: string;
  date: string; // YYYY-MM-DD
  hhmm: string; // "20:00"
  startLabel: string; // "8:00 PM"
  startIso: string; // full ISO from the DATE prop
  tour: string; // Notion TOUR label (verbatim)
  market: Market | null;
  guests: number | null;
  capacity: number | null;
  spotsLeft: number | null;
  status: string[];
  assignedGuideNames: string[];
  assignedGuidePeekId: string; // current Notion assignment (cmau_ id) — for two-way sync
  available: GuideRef[]; // guides who marked ✨
  unavailable: GuideRef[]; // guides who marked ❌
  excluded: boolean; // operator-excluded from the ✨/❌ picker
}

/** Mirrors functions/api/notion/availability.ts AvailabilityRow (structural). */
export interface AvailabilityRow {
  pageId: string;
  title: string;
  tour: string | null;
  market: string | null;
  status: string[];
  dateStart: string | null;
  startTimeLabel: string | null;
  slotKey: string;
  peekTimeslotId: string;
  guests: number | null;
  capacity: number | null;
  assignedGuideNames: string[];
  assignedGuidePeekId: string;
  checkboxes: Record<string, boolean>;
}

const AVAIL_PREFIX = "✨";
const UNAVAIL_PREFIX = "❌";

/** "8:00 PM" from "20:00". */
export function labelFromHHMM(hhmm: string): string {
  const [h, m] = hhmm.split(":").map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return hhmm;
  const ap = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 || 12;
  return `${h12}:${String(m).padStart(2, "0")} ${ap}`;
}

function toGuideRef(name: string): GuideRef | null {
  const g = lookupGuideByAnyName(name);
  if (!g) return null;
  return {
    id: g.id,
    peekName: g.peekName,
    availName: g.availName,
    region: g.region,
    pageId: guidePageIdByPeekName(g.peekName) ?? null,
  };
}

/** Build a GuideRef from a Peek accountUserId (cmau_…) — for Pull-from-Notion. */
export function guideRefById(id: string): GuideRef | null {
  const g = guideByPeekId[id];
  if (!g) return null;
  return {
    id: g.id,
    peekName: g.peekName,
    availName: g.availName,
    region: g.region,
    pageId: guidePageIdByPeekName(g.peekName) ?? null,
  };
}

// Resilience net: unique first-name token -> availName, derived from the roster
// (both Peek and availability identities). Catches an availability column named
// by a guide's other first name if the hand-maintained AVAIL_FIRST_TO_NAME ever
// drifts from the live DB again. Ambiguous tokens (shared first name) resolve to
// null so we refuse rather than mis-attribute.
const FIRST_TOKEN_TO_AVAILNAME: Record<string, string | null> = (() => {
  const m: Record<string, string | null> = {};
  const add = (token: string, availName: string) => {
    const k = token.toLowerCase();
    if (k in m && m[k] !== availName) m[k] = null;
    else if (!(k in m)) m[k] = availName;
  };
  for (const g of GUIDES) {
    add(g.peekName.split(" ")[0], g.availName);
    add(g.availName.split(" ")[0], g.availName);
  }
  return m;
})();

/** Parse a "✨ Mike" / "❌ Jade" column name -> the guide it refers to. */
function guideFromColumn(colName: string): { available: boolean; guide: GuideRef } | null {
  const trimmed = colName.trim();
  const available = trimmed.startsWith(AVAIL_PREFIX);
  const unavailable = trimmed.startsWith(UNAVAIL_PREFIX);
  if (!available && !unavailable) return null;
  const first = trimmed.slice(AVAIL_PREFIX.length).trim(); // both emojis are 1 code point
  // Availability columns key off first names; bridge to the full availName —
  // explicit map first (handles nicknames like "Dev"), then a roster-derived
  // token fallback so a renamed column can't silently drop a guide.
  const fullName =
    AVAIL_FIRST_TO_NAME[first] ?? FIRST_TOKEN_TO_AVAILNAME[first.toLowerCase()] ?? first;
  const guide = toGuideRef(fullName);
  if (!guide) return null;
  return { available, guide };
}

/** Exported for tests — pure projection of one Notion row into a PlanSlot. */
export function rowToSlot(row: AvailabilityRow): PlanSlot | null {
  const iso = row.dateStart || "";
  const date = iso.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return null; // undated rows can't be placed
  const hhmm = iso.length >= 16 ? iso.slice(11, 16) : "";
  const tour = row.tour || "";
  const startLabel = row.startTimeLabel || (hhmm ? labelFromHHMM(hhmm) : "");

  // Effective start time for the operator-exclusion check. Live DATE props are
  // often date-only/midnight with the real time in the START TIME label — the
  // raw hhmm would let an excluded 11:00 R66 slip past notionExcluded.
  const startMin = slotStartMin({ hhmm, startLabel });
  const exclHHMM =
    startMin != null
      ? `${String(Math.floor(startMin / 60)).padStart(2, "0")}:${String(startMin % 60).padStart(2, "0")}`
      : hhmm;

  let market: Market | null = null;
  try {
    if (row.market) market = canonMarket(row.market);
  } catch {
    market = null; // Team Mtg / unknown codes — surface as unmarked, never guess
  }

  const seen = new Set<string>();
  const available: GuideRef[] = [];
  const unavailable: GuideRef[] = [];
  for (const [colName, checked] of Object.entries(row.checkboxes)) {
    if (!checked) continue;
    const parsed = guideFromColumn(colName);
    if (!parsed) continue;
    const key = (parsed.available ? "a:" : "u:") + parsed.guide.id;
    if (seen.has(key)) continue;
    seen.add(key);
    (parsed.available ? available : unavailable).push(parsed.guide);
  }

  const spotsLeft =
    row.capacity != null && row.guests != null ? Math.max(row.capacity - row.guests, 0) : null;

  return {
    pageId: row.pageId,
    peekTimeslotId: row.peekTimeslotId || "",
    slotKey: row.slotKey || "",
    title: row.title || "",
    date,
    hhmm,
    startLabel,
    startIso: iso,
    tour,
    market,
    guests: row.guests,
    capacity: row.capacity,
    spotsLeft,
    status: row.status || [],
    assignedGuideNames: row.assignedGuideNames || [],
    assignedGuidePeekId: row.assignedGuidePeekId || "",
    available,
    unavailable,
    excluded: tour ? notionExcluded(tour, exclHHMM) : false,
  };
}

/** Fetch + project the Bookable Tours rows for a date window into PlanSlots. */
export async function fetchSlots(from: string, to: string): Promise<PlanSlot[]> {
  const res = await fetch(`/api/notion/availability?from=${from}&to=${to}`);
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new Error(`availability ${res.status}: ${body.slice(0, 200)}`);
  }
  const data = (await res.json()) as { rows?: AvailabilityRow[] };
  const slots = (data.rows || [])
    .map(rowToSlot)
    .filter((s): s is PlanSlot => s != null);
  slots.sort((a, b) => (a.date + a.hhmm).localeCompare(b.date + b.hhmm));
  return slots;
}
